import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  FileInput,
  Group,
  Modal,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconDownload,
  IconFileSpreadsheet,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from '../../api/client';

interface Props {
  opened: boolean;
  onClose: () => void;
}

interface BulkImportError {
  rowNumber: number;
  sku: string;
  message: string;
}

interface BulkImportResponse {
  totalRows: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: BulkImportError[];
}

// CSV header + one example row matching the server's expected schema.
// Kept in-file so the "download template" link doesn't need a static
// asset; the browser builds it on the fly via a Blob URL.
const TEMPLATE_CSV =
  'SKU,NameAr,NameEn,Description,CategoryNameAr,BasePrice,CostPrice,TaxGroupCode,UnitOfMeasureCode,IsTrackedInventory,ItemType,Barcodes\n' +
  'BRG-001,برغر كلاسيك,Classic Burger,شريحة لحم 150غ,وجبات,12.50,4.50,VAT16,PIECE,true,1,1234567890123;9876543210987\n';

export function BulkImportModal({ opened, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportResponse | null>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'items-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await apiClient.post<BulkImportResponse>(
        '/api/items/bulk-import',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(response.data);
      if (response.data.errors.length === 0) {
        // Wipe the items cache so the table re-fetches and shows the
        // freshly-imported rows. Bulk import is the rare case where
        // partial cache invalidation is more trouble than it's worth.
        qc.invalidateQueries({ queryKey: ['items'] });
        notifications.show({
          color: 'green',
          title: t('items.bulkImport.successTitle'),
          message: t('items.bulkImport.successMessage', {
            inserted: response.data.inserted,
            updated: response.data.updated,
          }),
        });
      }
    } catch (err) {
      const detail = isAxiosError(err)
        ? ((err.response?.data as { detail?: string; title?: string } | undefined)?.detail ??
           (err.response?.data as { title?: string } | undefined)?.title ??
           err.message)
        : t('common.toasts.errorBody');
      notifications.show({
        color: 'red',
        title: t('items.bulkImport.failTitle'),
        message: detail,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title={
        <Text fw={600} c="var(--wheels-ink-strong)">
          {t('items.bulkImport.title')}
        </Text>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('items.bulkImport.helpBody')}
        </Text>

        <Anchor component="button" type="button" onClick={downloadTemplate}>
          <Group gap={4}>
            <IconDownload size={14} stroke={1.6} />
            <Text size="sm" fw={500}>
              {t('items.bulkImport.downloadTemplate')}
            </Text>
          </Group>
        </Anchor>

        {!result && (
          <FileInput
            label={t('items.bulkImport.dropPrompt')}
            description={t('items.bulkImport.dropHint')}
            placeholder="items.csv"
            accept=".csv,text/csv"
            value={file}
            onChange={setFile}
            leftSection={<IconFileSpreadsheet size={16} stroke={1.6} />}
            size="md"
            clearable
          />
        )}

        {result && result.errors.length === 0 && (
          <Alert color="green" icon={<IconCheck size={16} stroke={1.8} />} variant="light">
            <Text size="sm" fw={500}>
              {t('items.bulkImport.successMessage', {
                inserted: result.inserted,
                updated: result.updated,
              })}
            </Text>
          </Alert>
        )}

        {result && result.errors.length > 0 && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} stroke={1.8} />}
            variant="light"
            title={t('items.bulkImport.errorsTitle')}
          >
            <Text size="sm" mb="xs">
              {t('items.bulkImport.errorsBody', {
                count: result.errors.length,
                total: result.totalRows,
              })}
            </Text>
            <Table verticalSpacing="xs" fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 60 }}>{t('items.bulkImport.colRow')}</Table.Th>
                  <Table.Th style={{ width: 120 }}>{t('items.bulkImport.colSku')}</Table.Th>
                  <Table.Th>{t('items.bulkImport.colMessage')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.errors.slice(0, 50).map((e, idx) => (
                  <Table.Tr key={`${e.rowNumber}-${idx}`}>
                    <Table.Td>
                      <Badge size="xs" variant="light" radius="sm" className="font-numeric">
                        {e.rowNumber}
                      </Badge>
                    </Table.Td>
                    <Table.Td className="font-mono">{e.sku || '—'}</Table.Td>
                    <Table.Td>{e.message}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {result.errors.length > 50 && (
              <Text size="xs" c="dimmed" mt="xs">
                {t('items.bulkImport.truncated', { shown: 50, total: result.errors.length })}
              </Text>
            )}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={submitting}
          >
            {t('common.close')}
          </Button>
          {result === null && (
            <Button
              onClick={submit}
              disabled={!file || submitting}
              loading={submitting}
            >
              {t('items.bulkImport.uploadCta')}
            </Button>
          )}
          {result !== null && (
            <Button onClick={reset}>
              {t('items.bulkImport.uploadAnother')}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
